import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { FaGraduationCap, FaArrowRight, FaStar, FaTrophy, FaRocket, FaTimes } from 'react-icons/fa';

const DegreeProgramsAd = () => {
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [currentText, setCurrentText] = useState(0);
  const [showCount, setShowCount] = useState(0);
  const [hasShownOnThisPage, setHasShownOnThisPage] = useState(false);

  const adTexts = [
    "🎓 Discover Your Future",
    "🚀 Launch Your Career",
    "💼 Industry-Ready Skills",
    "🏆 Accredited Programs",
    "⭐ World-Class Education"
  ];

  useEffect(() => {
    // Reset show count when page changes
    setShowCount(0);
    setHasShownOnThisPage(false);
    setIsVisible(false);
    setIsClosing(false);
  }, [location.pathname]);

  useEffect(() => {
    // Show ad after 5 seconds if not shown yet on this page
    if (!hasShownOnThisPage && showCount < 2) {
      const showTimer = setTimeout(() => {
        setIsVisible(true);
        setShowCount(prev => prev + 1);
        setHasShownOnThisPage(true);
      }, 5000);

      return () => clearTimeout(showTimer);
    }
  }, [hasShownOnThisPage, showCount]);

  useEffect(() => {
    // Auto-rotate text every 2 seconds
    const textTimer = setInterval(() => {
      setCurrentText((prev) => (prev + 1) % adTexts.length);
    }, 2000);

    return () => clearInterval(textTimer);
  }, []);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsVisible(false);
      // Show second time after 15 seconds if not shown twice yet
      if (showCount < 2) {
        setTimeout(() => {
          setIsVisible(true);
          setIsClosing(false);
          setShowCount(prev => prev + 1);
        }, 15000);
      }
    }, 300);
  };

  const handleClick = () => {
    // Redirect to EXEED website
    window.open('http://www.myexeed.com/', '_blank');
  };

  if (!isVisible) return null;

  return (
    <div 
      className={`advertisement-wrapper fixed bottom-4 left-2 sm:left-4 z-[30] transition-all duration-300 ${
        isClosing ? 'opacity-0 scale-95 translate-x-full' : 'opacity-100 scale-100 translate-x-0'
      }`} 
      style={{ 
        maxWidth: 'calc(100vw - 1rem)',
        pointerEvents: 'auto',
        isolation: 'isolate',
        contain: 'layout style paint'
      }}
    >
      <div 
        className="advertisement-container relative bg-gradient-to-br from-purple-600 via-pink-600 to-red-500 text-white rounded-xl sm:rounded-2xl shadow-2xl cursor-pointer overflow-hidden group"
        onClick={handleClick}
        style={{
          animation: 'pulse 2s infinite, bounce 3s infinite',
          width: '280px',
          height: '180px',
          maxWidth: 'calc(100vw - 2rem)'
        }}
      >
        {/* Animated Background */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 animate-shimmer"></div>
        
        {/* Close Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleClose();
          }}
          className="absolute top-2 right-2 z-10 bg-black/20 hover:bg-black/40 rounded-full p-1 transition-colors"
        >
          <FaTimes className="text-white text-xs" />
        </button>

        {/* Content */}
        <div className="relative p-3 sm:p-4 md:p-6 h-full flex flex-col justify-between">
          {/* Header */}
          <div className="flex items-center gap-2 sm:gap-3 mb-1 sm:mb-2">
            <div className="bg-white/20 p-1.5 sm:p-2 rounded-full">
              <FaGraduationCap className="text-lg sm:text-xl md:text-2xl animate-bounce" />
            </div>
            <div>
              <h3 className="font-bold text-sm sm:text-base md:text-lg leading-tight">Degree Programs</h3>
              <p className="text-xs sm:text-sm opacity-90">Explore Now!</p>
            </div>
          </div>

          {/* Rotating Text */}
          <div className="mb-2 sm:mb-3 md:mb-4">
            <div 
              key={currentText}
              className="text-lg sm:text-xl md:text-2xl font-bold animate-fadeIn"
              style={{
                background: 'linear-gradient(45deg, #fff, #ffd700, #fff)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}
            >
              {adTexts[currentText]}
            </div>
          </div>

          {/* Features */}
          <div className="flex items-center gap-2 sm:gap-3 md:gap-4 text-xs sm:text-sm">
            <div className="flex items-center gap-1">
              <FaStar className="text-yellow-300 text-xs sm:text-sm" />
              <span className="hidden sm:inline">8+ Programs</span>
              <span className="sm:hidden">8+</span>
            </div>
            <div className="flex items-center gap-1">
              <FaTrophy className="text-yellow-300 text-xs sm:text-sm" />
              <span className="hidden sm:inline">95% Success</span>
              <span className="sm:hidden">95%</span>
            </div>
            <div className="flex items-center gap-1">
              <FaRocket className="text-yellow-300 text-xs sm:text-sm" />
              <span className="hidden sm:inline">Career Ready</span>
              <span className="sm:hidden">Ready</span>
            </div>
          </div>

          {/* CTA Button */}
          <div className="flex items-center justify-between">
            <span className="text-xs sm:text-sm font-semibold">Click to Explore →</span>
            <div className="bg-white/20 hover:bg-white/30 rounded-full p-1.5 sm:p-2 transition-colors group-hover:scale-110">
              <FaArrowRight className="text-sm sm:text-base md:text-lg animate-pulse" />
            </div>
          </div>
        </div>

        {/* Floating Elements */}
        <div className="absolute top-2 sm:top-4 left-2 sm:left-4 w-1.5 h-1.5 sm:w-2 sm:h-2 bg-yellow-300 rounded-full animate-ping"></div>
        <div className="absolute bottom-2 sm:bottom-4 right-2 sm:right-4 w-1 h-1 bg-white rounded-full animate-ping" style={{animationDelay: '0.5s'}}></div>
        <div className="absolute top-8 sm:top-12 right-4 sm:right-8 w-1 h-1 sm:w-1.5 sm:h-1.5 bg-yellow-300 rounded-full animate-ping" style={{animationDelay: '1s'}}></div>
      </div>

      <style jsx>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        
        @keyframes bounce {
          0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
          40% { transform: translateY(-10px); }
          60% { transform: translateY(-5px); }
        }
        
        @keyframes shimmer {
          0% { transform: translateX(-100%) skewX(-12deg); }
          100% { transform: translateX(200%) skewX(-12deg); }
        }
        
        @keyframes fadeIn {
          0% { opacity: 0; transform: translateY(10px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        
        .animate-shimmer {
          animation: shimmer 3s infinite;
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.5s ease-in-out;
        }
        
        /* Prevent horizontal scroll */
        body {
          overflow-x: hidden !important;
        }
        
        html {
          overflow-x: hidden !important;
        }
        
        /* Advertisement styling - now properly positioned in component hierarchy */
        .advertisement-wrapper {
          position: fixed !important;
          z-index: 30 !important;
          pointer-events: auto !important;
          isolation: isolate !important;
          contain: layout style paint !important;
          transform: translateZ(0) !important;
          backface-visibility: hidden !important;
          will-change: transform !important;
        }
        
        /* Responsive sizing for advertisement */
        @media (min-width: 640px) {
          .advertisement-container {
            width: 320px !important;
            height: 200px !important;
          }
        }
        
        @media (min-width: 768px) {
          .advertisement-container {
            width: 350px !important;
            height: 220px !important;
          }
        }
        
        @media (max-width: 320px) {
          .advertisement-container {
            width: 260px !important;
            height: 160px !important;
          }
        }
        
        /* Mobile-specific positioning */
        @media (max-width: 768px) {
          .advertisement-wrapper {
            bottom: 1rem !important;
            left: 0.5rem !important;
            right: auto !important;
          }
        }
        
        /* Prevent advertisement from interfering with touch events */
        @media (max-width: 1024px) {
          .advertisement-wrapper {
            touch-action: manipulation !important;
          }
        }
        
        /* Clean, minimal styling now that component is properly positioned */
      `}</style>
    </div>
  );
};

export default DegreeProgramsAd;
